<script lang="ts">
    import {Flex} from 'shared/layouts';
    import type {ModalWindowActions} from 'shared/modal-dialogs';
    import type {IntervalString} from 'shared/ui-widgets/daterange-picker';
    import {getInjector} from 'src/core';
    import {LabeledValue} from 'src/features/erp-sutor/shared';
    import type {LogbookPairingBatchExportModalModel} from './types';
    import {ProjectLogbookPairingService} from './project-logbook-pairing.service';
    import KpModalHeaderCloseButton from 'src/modals/kp-modal/KpModalHeaderCloseButton.svelte';
    import KpModalContent from 'src/modals/kp-modal/KpModalContent.svelte';
    import KpModalTitle from 'src/modals/kp-modal/KpModalTitle.svelte';
    import KpModalFooterCloseButton from 'src/modals/kp-modal/KpModalFooterCloseButton.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import KpProgressBar from 'shared/ui-widgets/progress-bar/KpProgressBar.svelte';
    import KpValueEditor from 'shared/value-editors/kp-value-editor/KpValueEditor.svelte';
    import KpLoadingInline from 'shared/components/kp-loading/KpLoadingInline.svelte';

    export let model: LogbookPairingBatchExportModalModel;
    export let modalWindowActions: ModalWindowActions;

    const service = getInjector().getByClass(ProjectLogbookPairingService);

    let exporting = false;

    let divideExportsByDate = true;
    let replaceExisting = false;
    let selectedInterval: IntervalString = 'empty';
    $: canExport = selectedInterval !== 'empty';

    let totalLogbooksToExport = 0;
    let exportedLogbooks = 0;
    $: progressVal = totalLogbooksToExport === 0 ? 0 : exportedLogbooks / totalLogbooksToExport;

    const handleExportClick = async () => {
        await service.batchExportLogbooksToProjectFiles(model.projectRecord, selectedInterval, divideExportsByDate, replaceExisting, (totalLogbooks) => {
            exporting = true;
            totalLogbooksToExport = totalLogbooks;
        }, (done) => {
            exportedLogbooks = done;
        });

        exporting = false;
        modalWindowActions.acknowledge();
    };
</script>

<KpModalContent {modalWindowActions} additionalClasses="sutor-logbook-pairing-batch-export-modal">
    <svelte:fragment slot="header">
        <KpModalTitle>
            Hromadný export montážních deníků do souborů zakázky
        </KpModalTitle>

        {#if !exporting}
            <KpModalHeaderCloseButton/>
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="body">
        {#if !exporting}
            <LabeledValue withoutBiggerValueText label="Rozřadit exporty do složek dle MD">
                <KpValueEditor type="boolean" bind:model={divideExportsByDate}/>
            </LabeledValue>

            <Flex direction="column" gap="s">
                <LabeledValue withoutBiggerValueText label="Přepsat existující soubory">
                    <KpValueEditor type="boolean" bind:model={replaceExisting}/>
                </LabeledValue>

                <small class="text-muted">
                    Pokud nevyberete přepsání, soubory se vytvoří automaticky s předponou (2), (3), atd.
                </small>

                {#if replaceExisting}
                    <span class="text-danger">
                        Vaše staré exporty montážních deníků se stejným názvem souboru se smažou
                    </span>
                {/if}
            </Flex>

            <LabeledValue withoutBiggerValueText label="Rozmezí hromadného exportu">
                <KpValueEditor type="date-range" bind:model={selectedInterval}/>
            </LabeledValue>
        {:else}
            <Flex direction="row" alignItems="center" gap="m">
                <span>Probíhá hromadný export...</span>
                <KpLoadingInline size="xs"/>
            </Flex>

            <Flex direction="column" gap="sm">
                <KpProgressBar percentageValue={progressVal} showPercentage/>
                <span class="text-muted">Exportováno <span class="text-info">{exportedLogbooks}</span> z <span
                        class="text-info">{totalLogbooksToExport}</span> montážních deníků</span>
            </Flex>
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="footer">
        <KpButton isDisabled={!canExport}
                  isLoading={exporting}
                  tooltipLabel={!canExport ? 'Nejdříve vyberte rozmezí hromadného exportu' : null}
                  buttonStyle="brand-orange"
                  on:click={handleExportClick}>

            Exportovat
        </KpButton>

        {#if !exporting}
            <KpModalFooterCloseButton/>
        {/if}
    </svelte:fragment>
</KpModalContent>